import React, { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import { Camera, Fish, MapPin, Share2, Trash2, Plus, X, Check, Maximize2, ChevronRight, Book, DownloadCloud, PenTool, Droplets, Settings, ChevronDown, Circle, Sun, CloudRain, Cloud, Compass, Shield, Hourglass, Search, Menu, Trophy, LogOut, Type, CalendarDays, Ruler } from 'lucide-react';
import FiskekrokIkon from './components/FiskekrokIkon';

// Services
import firebaseService from './services/firebaseService';
import offlineQueue from './services/offlineQueue';
import snikService from './services/snikService';

// Komponenter
import LandingPage from './components/LandingPage';
import CaptureScreen from './components/CaptureScreen';
import DetailView from './components/DetailView';
import SharedView from './components/SharedView';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorBoundary from './components/ErrorBoundary';
import PrivacyBanner from './components/PrivacyBanner';
import PrivacySettings from './components/PrivacySettings';
import InstallPrompt from './components/InstallPrompt';
import UpdateToast from './components/UpdateToast';
import ContactModal from './components/ContactModal';
import InAppBrowserNotice from './components/InAppBrowserNotice';
import { isInAppBrowser } from './utils/browser';
import { num, fmtWeight } from './utils/format';

// Lazy-lastede tunge visninger (Leaflet / bok) — spares ved oppstart
const CatchMap = lazy(() => import('./components/CatchMap'));
const FishBook = lazy(() => import('./components/FishBook'));
const MemoryWall = lazy(() => import('./components/MemoryWall'));
const SnikView = lazy(() => import('./components/SnikView'));

// Hooks
import { useAuth } from './hooks/useAuth';
import { useCatches } from './hooks/useCatches';
import { useSharedCatch } from './hooks/useSharedCatch';

export default function App() {
  const [view, setView] = useState('dashboard');
  const [selectedCatch, setSelectedCatch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Fører i håndboka...');
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [error, setError] = useState(null);
  const [showPrivacySettings, setShowPrivacySettings] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [offlineNotice, setOfflineNotice] = useState(null);
  const [pendingCatches, setPendingCatches] = useState([]);
  const [captureHasData, setCaptureHasData] = useState(false);
  const [showCaptureConfirm, setShowCaptureConfirm] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [showBook, setShowBook] = useState(false);
  const [showWall, setShowWall] = useState(false);
  const [showSnik, setShowSnik] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [showInAppNotice, setShowInAppNotice] = useState(false);

  // Custom hooks
  const { user, loading: authLoading } = useAuth();
  const { catches, loading: catchesLoading } = useCatches(user);
  const { sharedCatch, loading: sharedLoading, error: sharedError, isShare } = useSharedCatch();

  // Sjekk samtykke ved oppstart
  useEffect(() => {
    const hasConsent = firebaseService.hasUserConsent();
    if (hasConsent) {
      // Bruker har samtykket tidligere — start analytics (laster GA + cookie)
      firebaseService.initAnalytics();
    }
  }, [user]);

  // History API — støtte for browser back-button
  useEffect(() => {
    const handlePopState = (event) => {
      const state = event.state;
      // Hvis vi er i capture-view og har data, vis bekreftelse
      if (view === 'capture' && captureHasData) {
        // Push tilbake til capture slik at brukeren forblir der
        window.history.pushState({ view: 'capture' }, '');
        setShowCaptureConfirm(true);
        return;
      }
      if (state?.view) {
        setView(state.view);
        setSelectedCatch(state.selectedCatch || null);
      } else {
        setView('dashboard');
        setSelectedCatch(null);
      }
    };

    window.addEventListener('popstate', handlePopState);

    // Sett initial state uten å pushe (replaceState)
    if (!window.history.state?.view) {
      window.history.replaceState({ view: 'dashboard' }, '');
    }

    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Push til history når view endres (unntatt ved popstate)
  const navigateTo = useCallback((newView, catchItem = null) => {
    setView(newView);
    setSelectedCatch(catchItem);
    if (newView !== 'capture') setCaptureHasData(false);
    window.history.pushState({ view: newView, selectedCatch: catchItem }, '');
  }, []);

  // Avbryt capture med bekreftelse
  const handleCaptureClose = useCallback(() => {
    if (captureHasData) {
      setShowCaptureConfirm(true);
    } else {
      navigateTo('dashboard');
    }
  }, [captureHasData, navigateTo]);

  // Hent ventende offline-fangster
  const refreshPending = useCallback(async () => {
    try {
      const items = await offlineQueue.getAll();
      setPendingCatches(items.map(item => ({
        ...item,
        _pending: true
      })));
    } catch (err) {
      console.error('Kunne ikke hente offline-kø:', err);
    }
  }, []);

  // Synkroniser offline-kø når bruker er innlogget og online
  useEffect(() => {
    if (!user) return;

    let syncTimer = null;

    const sync = async () => {
      try {
        const synced = await firebaseService.syncOfflineQueue();
        if (synced > 0) {
          console.log(`Synkroniserte ${synced} offline-fangster`);
        }
      } catch (err) {
        console.error('Offline sync feilet:', err);
      }
      try {
        const syncedSniker = await snikService.syncOfflineSniker();
        if (syncedSniker > 0) {
          console.log(`Synkroniserte ${syncedSniker} offline-sniker`);
        }
      } catch (err) {
        console.error('Offline snik-sync feilet:', err);
      }
      // Oppdater ventende-listen uansett
      refreshPending();
    };

    // Debounced sync — online-event kan fire flere ganger
    const debouncedSync = () => {
      if (syncTimer) clearTimeout(syncTimer);
      syncTimer = setTimeout(sync, 1000);
    };

    // Hent ventende ved oppstart
    refreshPending();

    // Sync ved oppstart
    sync();

    // Sync når nett kommer tilbake (debounced)
    window.addEventListener('online', debouncedSync);
    return () => {
      window.removeEventListener('online', debouncedSync);
      if (syncTimer) clearTimeout(syncTimer);
    };
  }, [user, refreshPending]);

  // Effekt for å vise delt fangst
  useEffect(() => {
    if (sharedCatch) {
      setView('shared-view');
    }
  }, [sharedCatch]);

  // Lagre fangst
  const handleSave = useCallback(async (data) => {
    if (!user) {
      setError('Du må være innlogget for å lagre fangster');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await firebaseService.saveCatch(data);
      if (result === 'offline-queued') {
        setOfflineNotice('Fangsten er lagret lokalt og synkroniseres når du er online igjen');
        setTimeout(() => setOfflineNotice(null), 5000);
        await refreshPending();
      }
      navigateTo('dashboard');
    } catch (error) {
      console.error('Lagring feilet:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Oppdater eksisterende fangst
  const handleUpdate = useCallback(async (catchId, data) => {
    if (!user) {
      setError('Du må være innlogget for å redigere fangster');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await firebaseService.updateCatch(catchId, data);
      navigateTo('dashboard');
    } catch (error) {
      console.error('Oppdatering feilet:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [user, navigateTo]);

  // Opprett deling
  const handleCreateShare = useCallback(async (catchItem) => {
    setLoading(true);
    setError(null);

    try {
      const shareId = await firebaseService.createShare(catchItem);
      // Path-basert lenke (ikke ?share=) slik at Firebase Hosting kan rute
      // den til sharePreview-funksjonen og gi riktig forhåndsvisning på
      // sosiale medier / iMessage. Se functions/index.js for detaljer.
      const url = `${window.location.origin}/dele/${shareId}`;
      
      // 1. Prøv native delingsark (best på mobil)
      if (navigator.share) {
        try {
          await navigator.share({
            title: `${catchItem.species} — FiskDa`,
            text: `Sjekk ut denne fangsten jeg fikk!`,
            url: url,
          });
          // Delt via OS-delingsark — ingenting ble kopiert, så vis en generisk
          // "delt"-bekreftelse i stedet for "LINK KOPIERT" (var misvisende).
          setCopyFeedback('shared');
          setTimeout(() => setCopyFeedback(false), 3000);
          return;
        } catch (shareErr) {
          // Bruker avbrøt delingsarket — fall gjennom til clipboard
          if (shareErr.name === 'AbortError') {
            return;
          }
        }
      }

      // 2. Prøv clipboard API
      if (navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(url);
          setCopyFeedback('copied');
          setTimeout(() => setCopyFeedback(false), 3000);
          return;
        } catch (clipErr) {
          // Clipboard feilet — fall gjennom til manuell fallback
        }
      }

      // 3. Manuell fallback (fungerer overalt)
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopyFeedback('copied');
      setTimeout(() => setCopyFeedback(false), 3000);

    } catch (error) {
      console.error('Deling feilet:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Slett fangst
  const handleDelete = useCallback(async (catchId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne fangsten?')) {
      return;
    }

    setLoading(true);
    setLoadingMessage('Fjerner fra håndboken...');
    setError(null);

    try {
      await firebaseService.deleteCatch(catchId);
      navigateTo('dashboard');
    } catch (error) {
      console.error('Sletting feilet:', error);
      setError(error.message);
    } finally {
      setLoading(false);
      setLoadingMessage('Fører i håndboka...');
    }
  }, []);

  // Handle Google Sign In
  const handleSignIn = async () => {
    // Google blokkerer OAuth i in-app-nettlesere (Snapchat, Instagram osv.)
    // med "403: disallowed_useragent". Vis heller en hjelpemelding.
    if (isInAppBrowser()) {
      setShowInAppNotice(true);
      return;
    }

    setSigningIn(true);
    setError(null);
    try {
      await firebaseService.signInWithGoogle();
    } catch (error) {
      console.error('Innlogging feilet:', error);
      setError(error.message);
    } finally {
      setSigningIn(false);
    }
  };

  // Handle Sign Out
  const handleSignOut = async () => {
    try {
      await firebaseService.signOut();
      navigateTo('dashboard');
    } catch (error) {
      console.error('Utlogging feilet:', error);
      setError(error.message);
    }
  };

  // Vis loading mens appen initialiseres
  if (authLoading) {
    return <LoadingSpinner message="Starter FiskDa..." />;
  }

  // Delt fangst er OFFENTLIG — vises uten innlogging.
  // Dette må sjekkes FØR landing page, ellers havner mottakeren av en delt
  // lenke alltid på forsiden i stedet for å se fangsten.
  // Vi bruker isShare (stabil markør fra hooken) i stedet for å lese URL-en på
  // nytt, fordi ?share= fjernes fra adresselinjen etter at delingen er hentet.
  if (isShare || sharedCatch) {
    if (sharedLoading) {
      return <LoadingSpinner message="Laster delt fangst..." />;
    }
    if (sharedCatch) {
      return (
        <ErrorBoundary>
          <div className="min-h-screen bg-[#F8F5F0] text-[#2D3436] font-serif relative overflow-x-hidden">
            <div className="fixed inset-0 opacity-[0.03] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/felt.png')] z-[50]"></div>
            <main className="flex-1 w-full max-w-5xl mx-auto px-4 py-8 z-10 font-sans">
              <SharedView item={sharedCatch} onZoom={setZoomedImage} loading={false} />
            </main>
            {zoomedImage && (
              <ImageZoomModal image={zoomedImage} onClose={() => setZoomedImage(null)} />
            )}
          </div>
        </ErrorBoundary>
      );
    }
    // Lenken finnes men fangsten kunne ikke hentes (utløpt/ugyldig)
    if (sharedError) {
      return (
        <div className="min-h-screen bg-[#F8F5F0] text-[#2D3436] font-serif flex flex-col items-center justify-center gap-6 px-6 text-center">
          <p className="font-serif italic text-2xl text-[#3B4D3C]">
            Denne delte fangsten finnes ikke lenger.
          </p>
          <p className="font-sans text-sm text-[#2D3436]/60 max-w-sm">
            Delte fangster utløper etter 30 dager, eller lenken kan være feil.
          </p>
          <button
            onClick={() => { window.location.href = window.location.origin + window.location.pathname; }}
            className="bg-[#3B4D3C] text-[#F8F5F0] px-10 py-4 rounded shadow-xl font-serif font-black italic text-lg hover:scale-105 active:scale-95 transition-all"
          >
            Gå til FiskDa
          </button>
        </div>
      );
    }
  }

  // Vis landing page hvis ikke logget inn
  if (!user) {
    return (
      <>
        <LandingPage onSignIn={handleSignIn} loading={signingIn} error={error} onDismissError={() => setError(null)} />
        {showInAppNotice && (
          <InAppBrowserNotice onClose={() => setShowInAppNotice(false)} />
        )}
      </>
    );
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-[#F8F5F0] text-[#2D3436] flex flex-col font-serif relative overflow-x-hidden">
        {/* Tekstur overlay */}
        <div className="fixed inset-0 opacity-[0.03] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/felt.png')] z-[50]"></div>
        
        {/* Header */}
        <Header 
          view={view} 
          onNavigate={setView} 
          onClose={view === 'capture' ? handleCaptureClose : null}
          onShowPrivacySettings={() => setShowPrivacySettings(true)}
          onShowMap={() => setShowMap(true)}
          onShowBook={() => setShowBook(true)}
          onShowWall={() => setShowWall(true)}
          onShowSnik={() => setShowSnik(true)}
          onToggleSearch={() => setShowSearch(s => !s)}
          searchActive={showSearch}
          onSignOut={handleSignOut}
        />
        
        {/* Error display */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 mx-4 mt-4 rounded">
            <p className="font-medium">{error}</p>
            <button 
              onClick={() => setError(null)}
              className="text-red-500 hover:text-red-700 text-sm mt-1"
            >
              Lukk
            </button>
          </div>
        )}

        {/* Offline notice */}
        {offlineNotice && (
          <div className="bg-amber-50 border border-amber-300 text-amber-800 px-4 py-3 mx-4 mt-4 rounded font-sans text-sm flex items-center gap-2">
            <span>📡</span>
            <p className="flex-1">{offlineNotice}</p>
            <button onClick={() => setOfflineNotice(null)} className="text-amber-600 hover:text-amber-800 text-xs">Lukk</button>
          </div>
        )}

        {/* Main content */}
        <main className="flex-1 w-full max-w-5xl mx-auto px-4 py-8 pb-44 z-10 font-sans">
          {view === 'dashboard' && (
            <Dashboard 
              catches={catches}
              pendingCatches={pendingCatches}
              loading={catchesLoading}
              search={searchQuery}
              setSearch={setSearchQuery}
              showSearch={showSearch}
              onCloseSearch={() => { setShowSearch(false); setSearchQuery(''); }}
              onSelectCatch={(catch_) => {
                navigateTo('detail', catch_);
              }}
            />
          )}

          {view === 'capture' && (
            <CaptureScreen 
              onSave={handleSave} 
              onCancel={() => navigateTo('dashboard')}
              onFormChange={(hasData) => setCaptureHasData(hasData)}
              loading={loading}
              catches={catches}
            />
          )}

          {view === 'detail' && selectedCatch && (
            <DetailView 
              item={selectedCatch}
              onShare={handleCreateShare}
              onDelete={handleDelete}
              onEdit={(item) => navigateTo('edit', item)}
              onZoom={setZoomedImage}
              copyFeedback={copyFeedback}
            />
          )}

          {view === 'edit' && selectedCatch && (
            <CaptureScreen 
              mode="edit"
              initialData={selectedCatch}
              onSave={handleUpdate}
              onCancel={() => navigateTo('detail', selectedCatch)}
              onFormChange={(hasData) => setCaptureHasData(hasData)}
              loading={loading}
              catches={catches}
            />
          )}

          {view === 'shared-view' && sharedCatch && (
            <SharedView 
              item={sharedCatch} 
              onZoom={setZoomedImage}
              loading={sharedLoading}
            />
          )}
        </main>

        {/* Floating action button */}
        {view === 'dashboard' && (
          <div className="fixed bottom-12 left-0 right-0 flex justify-center pointer-events-none z-[110]">
            <button 
              onClick={() => navigateTo('capture')}
              className="w-20 h-20 bg-[#E67E22] text-[#F8F5F0] rounded-full flex items-center justify-center shadow-[0_15px_40px_rgba(230,126,34,0.3)] hover:scale-110 active:scale-95 transition-transform duration-200 pointer-events-auto border-8 border-[#F8F5F0]"
              aria-label="Ny fangst"
            >
              <Plus size={40} strokeWidth={3} />
            </button>
          </div>
        )}

        {/* Image zoom modal */}
        {zoomedImage && (
          <ImageZoomModal 
            image={zoomedImage} 
            onClose={() => setZoomedImage(null)} 
          />
        )}

        {/* Loading overlay */}
        {loading && (
          <LoadingSpinner message={loadingMessage} overlay />
        )}

        {/* Bekreftelse ved avbryt capture */}
        {showCaptureConfirm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[300] flex items-center justify-center p-4">
            <div className="bg-[#F8F5F0] border-2 border-[#3B4D3C] rounded-lg shadow-2xl max-w-sm w-full p-8 animate-in fade-in duration-300">
              <h3 className="font-serif text-xl font-black italic text-[#3B4D3C] mb-3">
                Forkast registrering?
              </h3>
              <p className="font-sans text-sm text-[#2D3436]/70 mb-6">
                Du har begynt å fylle ut en fangst. Vil du forkaste den?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCaptureConfirm(false)}
                  className="flex-1 py-3 rounded border border-[#D1CCC0] font-sans font-semibold text-sm text-[#3B4D3C] hover:bg-white transition-colors"
                >
                  Fortsett
                </button>
                <button
                  onClick={() => { setShowCaptureConfirm(false); navigateTo('dashboard'); }}
                  className="flex-1 py-3 rounded bg-red-600 text-white font-sans font-semibold text-sm hover:bg-red-700 transition-colors"
                >
                  Forkast
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Privacy banner */}
        <PrivacyBanner />

        {/* Install prompt for PWA */}
        <InstallPrompt />

        {/* Toast når en ny versjon er klar */}
        <UpdateToast />

        {/* Privacy settings modal */}
        {showPrivacySettings && (
          <PrivacySettings 
            user={user}
            onClose={() => setShowPrivacySettings(false)}
            onOpenContact={() => setShowContactModal(true)}
          />
        )}

        {/* Contact modal */}
        {showContactModal && (
          <ContactModal onClose={() => setShowContactModal(false)} />
        )}

        {/* Kart — Hvor har jeg fått fisk */}
        {showMap && (
          <Suspense fallback={<LoadingSpinner message="Laster kart..." overlay />}>
            <CatchMap
              catches={catches}
              onClose={() => setShowMap(false)}
              onSelectCatch={(item) => {
                setShowMap(false);
                navigateTo('detail', item);
              }}
            />
          </Suspense>
        )}

        {/* Den store kloke boken */}
        {showBook && (
          <Suspense fallback={<LoadingSpinner message="Åpner boken..." overlay />}>
            <FishBook onClose={() => setShowBook(false)} />
          </Suspense>
        )}

        {/* Minneveggen — dine høydepunkter */}
        {showWall && (
          <Suspense fallback={<LoadingSpinner message="Henger opp bildene..." overlay />}>
            <MemoryWall
              catches={catches}
              onClose={() => setShowWall(false)}
              onSelectCatch={(item) => {
                setShowWall(false);
                navigateTo('detail', item);
              }}
            />
          </Suspense>
        )}

        {/* Hvor er sniken? */}
        {showSnik && (
          <Suspense fallback={<LoadingSpinner message="Åpner Snikkart..." overlay />}>
            <SnikView user={user} onClose={() => setShowSnik(false)} />
          </Suspense>
        )}
      </div>
    </ErrorBoundary>
  );
}

// Header komponent
function Header({ view, onNavigate, onClose, onShowPrivacySettings, onShowMap, onShowBook, onShowWall, onShowSnik, onToggleSearch, searchActive, onSignOut }) {
  const [showProfileMenu, setShowProfileMenu] = React.useState(false);
  const [showMobileMenu, setShowMobileMenu] = React.useState(false);

  return (
    <header className="w-full z-[100] sticky top-0 border-b border-[#D1CCC0] bg-[#F8F5F0]/95 backdrop-blur-md pt-safe">
      <div className="max-w-5xl mx-auto px-6 py-5 flex justify-between items-center">
        <div 
          className="flex items-center gap-4 cursor-pointer group" 
          onClick={() => onNavigate('dashboard')}
        >
          <div className="p-1 rounded-xl shadow-md transform -rotate-2 transition-transform group-hover:rotate-0 bg-[#F8F5F0]">
            <img
              src="/logo-mark.webp"
              alt="FiskDa"
              width={44}
              height={44}
              className="w-11 h-11 object-contain block rounded-lg"
            />
          </div>
          <div className="flex flex-col">
            <h1 className="font-bold tracking-[0.05em] text-2xl leading-none italic text-[#3B4D3C]">
              FiskDa
            </h1>
            <span className="font-sans text-[8px] uppercase tracking-[0.4em] text-[#E67E22] font-black mt-1">
              Skitt fiske!
            </span>
          </div>
        </div>
        
        <div className="flex items-center gap-2 relative">
          {/* ===== DESKTOP: knapper inline (skjult på mobil) ===== */}
          {view === 'dashboard' && (
            <div className="hidden sm:flex items-center gap-2">
              <button
                onClick={onToggleSearch}
                className={`p-2 rounded-full transition-all ${
                  searchActive
                    ? 'bg-[#3B4D3C] text-[#F8F5F0] shadow-md'
                    : 'hover:bg-[#D1CCC0]/20 text-[#3B4D3C]'
                }`}
                aria-label="Søk i fangster"
                title="Søk i fangster"
              >
                <Search size={22} strokeWidth={1.5} />
              </button>
              <button
                onClick={onShowMap}
                className="p-2 hover:bg-[#D1CCC0]/20 rounded-full transition-all"
                aria-label="Hvor har jeg fått fisk"
                title="Hvor har jeg fått fisk"
              >
                <MapPin size={22} strokeWidth={1.5} className="text-[#3B4D3C]" />
              </button>
              <button
                onClick={onShowBook}
                className="p-2 hover:bg-[#D1CCC0]/20 rounded-full transition-all"
                aria-label="Den store kloke boken"
                title="Den store kloke boken"
              >
                <Book size={22} strokeWidth={1.5} className="text-[#3B4D3C]" />
              </button>

              <button
                onClick={onShowWall}
                className="p-2 hover:bg-[#D1CCC0]/20 rounded-full transition-all"
                aria-label="Minneveggen"
                title="Minneveggen"
              >
                <Trophy size={22} strokeWidth={1.5} className="text-[#3B4D3C]" />
              </button>
              <button
                onClick={onShowSnik}
                className="p-2 hover:bg-[#D1CCC0]/20 rounded-full transition-all"
                aria-label="Hvor er sniken?"
                title="Hvor er sniken?"
              >
                <FiskekrokIkon className="w-[22px] h-[22px] text-[#3B4D3C]" strokeWidth={1.5} />
              </button>

              {/* Profil-knapp med dropdown */}
              <div className="relative">
                <button 
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="p-2 hover:bg-[#D1CCC0]/20 rounded-full transition-all"
                  aria-label="Profil"
                  title="Profil"
                >
                  <Fish size={24} strokeWidth={1.5} className="text-[#3B4D3C]" />
                </button>
                
                {showProfileMenu && (
                  <div className="fiskda-menu-pop absolute right-0 top-full mt-2 w-48 bg-white border border-[#D1CCC0] shadow-xl rounded-lg overflow-hidden z-[200] origin-top-right">
                    <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        onShowPrivacySettings();
                      }}
                      className="w-full px-4 py-3 text-left font-sans text-sm hover:bg-[#F8F5F0] transition-colors flex items-center gap-2 text-[#3B4D3C]"
                    >
                      <Shield size={16} />
                      Personvern
                    </button>
                    <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        onSignOut();
                      }}
                      className="w-full px-4 py-3 text-left font-sans text-sm hover:bg-[#F8F5F0] transition-colors border-t border-[#D1CCC0] text-[#E67E22] font-semibold flex items-center gap-2"
                    >
                      <LogOut size={16} strokeWidth={1.5} />
                      Logg ut
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ===== MOBIL: alt samlet bak én meny-knapp ===== */}
          {view === 'dashboard' && (
            <div className="sm:hidden relative">
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className={`p-2 rounded-full transition-all ${
                  showMobileMenu
                    ? 'bg-[#3B4D3C] text-[#F8F5F0] shadow-md'
                    : 'hover:bg-[#D1CCC0]/20 text-[#3B4D3C]'
                }`}
                aria-label="Meny"
                aria-expanded={showMobileMenu}
                title="Meny"
              >
                {showMobileMenu ? <X size={24} strokeWidth={1.5} /> : <Menu size={24} strokeWidth={1.5} />}
              </button>

              {showMobileMenu && (
                <div className="fiskda-menu-pop absolute right-0 top-full mt-2 w-56 bg-white border border-[#D1CCC0] shadow-xl rounded-lg overflow-hidden z-[200] origin-top-right">
                  <button
                    onClick={() => { setShowMobileMenu(false); onToggleSearch(); }}
                    className="w-full px-4 py-3 text-left font-sans text-sm hover:bg-[#F8F5F0] transition-colors flex items-center gap-3 text-[#3B4D3C]"
                  >
                    <Search size={18} strokeWidth={1.5} />
                    Søk i fangster
                  </button>
                  <button
                    onClick={() => { setShowMobileMenu(false); onShowMap(); }}
                    className="w-full px-4 py-3 text-left font-sans text-sm hover:bg-[#F8F5F0] transition-colors flex items-center gap-3 text-[#3B4D3C] border-t border-[#D1CCC0]"
                  >
                    <MapPin size={18} strokeWidth={1.5} />
                    Hvor har jeg fått fisk
                  </button>
                  <button
                    onClick={() => { setShowMobileMenu(false); onShowBook(); }}
                    className="w-full px-4 py-3 text-left font-sans text-sm hover:bg-[#F8F5F0] transition-colors flex items-center gap-3 text-[#3B4D3C] border-t border-[#D1CCC0]"
                  >
                    <Book size={18} strokeWidth={1.5} />
                    Den store kloke boken
                  </button>
                  <button
                    onClick={() => { setShowMobileMenu(false); onShowWall(); }}
                    className="w-full px-4 py-3 text-left font-sans text-sm hover:bg-[#F8F5F0] transition-colors flex items-center gap-3 text-[#3B4D3C] border-t border-[#D1CCC0]"
                  >
                    <Trophy size={18} strokeWidth={1.5} />
                    Minneveggen
                  </button>
                  <button
                    onClick={() => { setShowMobileMenu(false); onShowSnik(); }}
                    className="w-full px-4 py-3 text-left font-sans text-sm hover:bg-[#F8F5F0] transition-colors flex items-center gap-3 text-[#3B4D3C] border-t border-[#D1CCC0]"
                  >
                    <FiskekrokIkon className="w-[18px] h-[18px]" strokeWidth={1.5} />
                    Hvor er sniken?
                  </button>
                  <button
                    onClick={() => { setShowMobileMenu(false); onShowPrivacySettings(); }}
                    className="w-full px-4 py-3 text-left font-sans text-sm hover:bg-[#F8F5F0] transition-colors flex items-center gap-3 text-[#3B4D3C] border-t border-[#D1CCC0]"
                  >
                    <Shield size={18} strokeWidth={1.5} />
                    Personvern
                  </button>
                  <button
                    onClick={() => { setShowMobileMenu(false); onSignOut(); }}
                    className="w-full px-4 py-3 text-left font-sans text-sm hover:bg-[#F8F5F0] transition-colors flex items-center gap-3 text-[#E67E22] font-semibold border-t border-[#D1CCC0]"
                  >
                    <LogOut size={18} strokeWidth={1.5} />
                    Logg ut
                  </button>
                </div>
              )}
            </div>
          )}
          
          {view !== 'dashboard' && (
            <button 
              onClick={onClose || (() => onNavigate('dashboard'))} 
              className="p-2 hover:bg-[#D1CCC0]/20 rounded-full transition-all"
              aria-label="Tilbake til dashboard"
            >
              <X size={26} strokeWidth={1.5} />
            </button>
          )}
        </div>
      </div>
      
      {/* Overlay som lukker menyene ved klikk utenfor */}
      {(showProfileMenu || showMobileMenu) && (
        <div 
          className="fixed inset-0 z-[150]" 
          onClick={() => { setShowProfileMenu(false); setShowMobileMenu(false); }}
        />
      )}
    </header>
  );
}

// Bygg én søkbar tekststreng fra alle relevante felter i en fangst
function buildSearchText(c) {
  const parts = [
    c.species,
    c.latin,
    c.nickname,
    c.waterType === 'salt' ? 'saltvann' : c.waterType === 'fresh' ? 'ferskvann' : '',
    c.weight ? `${c.weight} gram g` : '',
    c.weather?.condition,
    c.weather?.windDir,
    c.advanced?.gear,
    c.advanced?.lure,
    c.advanced?.tide,
    c.advanced?.notes,
  ];
  if (c.caughtAt) {
    const d = new Date(c.caughtAt);
    // Dato i flere former: 2024, mars, 03, 15.03.2024
    parts.push(d.toLocaleDateString('no-NO'));
    parts.push(d.toLocaleDateString('no-NO', { month: 'long' }));
    parts.push(String(d.getFullYear()));
  }
  return parts.filter(Boolean).join(' ').toLowerCase();
}

// Filtrer fangster på et fritekstsøk (alle ord må matche)
function filterCatches(list, query) {
  const q = query.trim().toLowerCase();
  if (!q) return list;
  const tokens = q.split(/\s+/);
  return list.filter((c) => {
    const text = buildSearchText(c);
    return tokens.every((t) => text.includes(t));
  });
}

// Sorteringsvalg for fangstoversikten
const SORT_OPTIONS = [
  { key: 'date', label: 'Dato', icon: CalendarDays },
  { key: 'name', label: 'Navn / fisketur', icon: Type },
  { key: 'size', label: 'Størrelse', icon: Ruler },
];

// Standard sorteringsretning per felt (dato og størrelse: størst/nyest først)
const DEFAULT_SORT_DIR = { name: 'asc', date: 'desc', size: 'desc' };

// Hent sammenlignbar verdi for et gitt sorteringsfelt
function getSortValue(c, key) {
  switch (key) {
    case 'name':
      return (c.nickname || c.species || '').toLowerCase();
    case 'date':
      return c.caughtAt || 0;
    case 'size': {
      const w = num(c.weight);
      return w == null ? -Infinity : w;
    }
    default:
      return 0;
  }
}

// Sorter en liste med fangster på valgt felt/retning
function sortCatches(list, sortBy, sortDir) {
  const sorted = [...list].sort((a, b) => {
    const va = getSortValue(a, sortBy);
    const vb = getSortValue(b, sortBy);
    if (typeof va === 'string' || typeof vb === 'string') {
      return String(va).localeCompare(String(vb), 'no');
    }
    return va - vb;
  });
  return sortDir === 'desc' ? sorted.reverse() : sorted;
}

// Dashboard komponent
function Dashboard({ catches, pendingCatches, loading, search, setSearch, showSearch, onCloseSearch, onSelectCatch }) {
  const totalCount = catches.length + pendingCatches.length;
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState(DEFAULT_SORT_DIR.date);

  const handleSortChange = (key) => {
    if (key === sortBy) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir(DEFAULT_SORT_DIR[key]);
    }
  };

  // Filtrer både lagrede og ventende fangster
  const filteredCatches = useMemo(() => filterCatches(catches, search), [catches, search]);
  const filteredPending = useMemo(() => filterCatches(pendingCatches, search), [pendingCatches, search]);

  // Sorter etter valgt felt/retning
  const sortedCatches = useMemo(
    () => sortCatches(filteredCatches, sortBy, sortDir),
    [filteredCatches, sortBy, sortDir]
  );
  const sortedPending = useMemo(
    () => sortCatches(filteredPending, sortBy, sortDir),
    [filteredPending, sortBy, sortDir]
  );

  const resultCount = filteredCatches.length + filteredPending.length;
  const searching = search.trim().length > 0;

  if (loading) {
    return <LoadingSpinner message="Laster fangster..." />;
  }

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4">
      <div className="flex justify-between items-end border-b border-[#3B4D3C]/20 pb-4">
        <div className="flex items-center gap-3">
          <Book size={24} className="text-[#3B4D3C]" />
          <h2 className="font-serif text-3xl font-black italic tracking-tight text-[#3B4D3C]">
            Min Fangst
          </h2>
        </div>
        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#3B4D3C]/40 italic">
          {searching ? `${resultCount} av ${totalCount}` : `${totalCount} registreringer`}
        </p>
      </div>

      {/* Smart søk — folder ut fra forstørrelsesglasset i headeren */}
      {showSearch && totalCount > 0 && (
        <div className="animate-in fade-in slide-in-from-top-2 duration-300">
          <div className="flex items-center gap-3 bg-white px-5 py-4 rounded-lg border-2 border-[#3B4D3C]/30 shadow-lg focus-within:border-[#E67E22] transition-colors">
            <Search size={20} className="text-[#E67E22] shrink-0" />
            <input
              type="text"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') onCloseSearch(); }}
              placeholder="Søk — art, fisketur, vær, sted, dato..."
              className="bg-transparent w-full font-serif italic text-lg outline-none placeholder:text-[#3B4D3C]/30"
              aria-label="Søk i fangster"
            />
            {searching && (
              <span className="font-sans text-[10px] uppercase tracking-widest text-[#3B4D3C]/40 shrink-0 hidden sm:inline">
                {resultCount} treff
              </span>
            )}
            <button
              onClick={onCloseSearch}
              className="text-[#3B4D3C]/40 hover:text-[#3B4D3C] transition-colors shrink-0"
              aria-label="Lukk søk"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      )}

      {totalCount > 0 && resultCount > 0 && (
        <SortBar sortBy={sortBy} sortDir={sortDir} onChange={handleSortChange} />
      )}

      {totalCount === 0 ? (
        <EmptyState />
      ) : resultCount === 0 ? (
        <NoSearchResults query={search} onClear={onCloseSearch} />
      ) : (
        <CatchGrid catches={sortedCatches} pendingCatches={sortedPending} onSelectCatch={onSelectCatch} />
      )}
    </div>
  );
}

// Sorteringsvalg — knapperad med aktivt felt highlighted og pil for retning
function SortBar({ sortBy, sortDir, onChange }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#3B4D3C]/40 italic shrink-0">
        Sorter
      </span>
      {SORT_OPTIONS.map((opt) => {
        const active = sortBy === opt.key;
        const Icon = opt.icon;
        return (
          <button
            key={opt.key}
            onClick={() => onChange(opt.key)}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-sans font-semibold transition-all ${
              active
                ? 'bg-[#E67E22] text-white shadow-sm'
                : 'bg-white text-[#3B4D3C]/60 border border-[#D1CCC0]/60 hover:border-[#3B4D3C]/40'
            }`}
            aria-pressed={active}
          >
            <Icon size={11} strokeWidth={2.25} />
            <span className="hidden sm:inline">{opt.label}</span>
            <ChevronDown
              size={11}
              strokeWidth={2.5}
              className={`transition-transform duration-200 ${active ? 'opacity-100' : 'opacity-0'} ${
                sortDir === 'asc' ? 'rotate-180' : 'rotate-0'
              }`}
            />
          </button>
        );
      })}
    </div>
  );
}

// Ingen treff på søk
function NoSearchResults({ query, onClear }) {
  return (
    <div className="py-24 text-center flex flex-col items-center gap-6">
      <div className="p-6 bg-white rounded-full border border-[#D1CCC0] border-dashed">
        <Search size={48} strokeWidth={1} className="text-[#3B4D3C]/40" />
      </div>
      <p className="font-serif italic text-lg text-[#3B4D3C] max-w-xs">
        Ingen fangster matcher "{query}".
      </p>
      <button
        onClick={onClear}
        className="font-sans text-xs uppercase tracking-widest text-[#E67E22] font-bold hover:underline"
      >
        Tøm søk
      </button>
    </div>
  );
}

// Tom tilstand
function EmptyState() {
  return (
    <div className="py-32 text-center opacity-30 flex flex-col items-center gap-8">
      <div className="p-8 bg-white rounded-full border border-[#D1CCC0] border-dashed">
        <PenTool size={64} strokeWidth={1} className="text-[#3B4D3C]" />
      </div>
      <p className="font-serif italic text-xl max-w-xs mx-auto text-[#3B4D3C]">
        Ingen fangster i loggen ennå. Skitt fiske!
      </p>
    </div>
  );
}

// Fangst-grid
function CatchGrid({ catches, pendingCatches, onSelectCatch }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-8">
      {pendingCatches.map(catch_ => (
        <CatchCard 
          key={`pending-${catch_.id}`} 
          catch_={catch_} 
          pending
        />
      ))}
      {catches.map(catch_ => (
        <CatchCard 
          key={catch_.id} 
          catch_={catch_} 
          onClick={() => onSelectCatch(catch_)} 
        />
      ))}
    </div>
  );
}

// Fangstbilde med skeleton-shimmer og mykt innton.
// Viser en rolig lastetilstand til bildet faktisk er lastet (eller cachet),
// slik at bildene ikke "popper" brått inn. Faller tilbake til fiske-ikon ved feil.
function CatchImage({ src, alt, pending }) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  return (
    <div className={`relative aspect-[4/5] bg-[#F8F5F0] overflow-hidden mb-4 ${pending ? 'grayscale' : ''}`}>
      {/* Skeleton vises til bildet er lastet */}
      {!loaded && !failed && (
        <div className="absolute inset-0 fiskda-img-skeleton" aria-hidden="true" />
      )}

      {failed ? (
        <div className="absolute inset-0 flex items-center justify-center text-[#D1CCC0]">
          <Fish size={40} strokeWidth={1} />
        </div>
      ) : (
        <img
          src={src}
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          className={`w-full h-full object-cover transition-opacity duration-500 ${
            loaded ? 'opacity-100' : 'opacity-0'
          } ${
            pending
              ? 'grayscale opacity-60'
              : 'grayscale-[0.1] group-hover:grayscale-0'
          }`}
          alt={alt}
          loading="lazy"
          decoding="async"
        />
      )}
    </div>
  );
}

// Fangst-kort
function CatchCard({ catch_, onClick, pending }) {
  return (
    <div 
      onClick={pending ? undefined : onClick}
      className={`bg-white p-3 shadow-md transition-all border border-[#D1CCC0]/40 group relative ${
        pending 
          ? 'opacity-50 cursor-default' 
          : 'hover:shadow-2xl cursor-pointer transform hover:-translate-y-1'
      }`}
    >
      {pending && (
        <div className="absolute top-2 right-2 z-10 bg-white/90 rounded-full p-1.5 shadow-sm border border-[#D1CCC0]/60">
          <Hourglass size={14} className="text-[#E67E22] animate-pulse" />
        </div>
      )}
      <CatchImage src={catch_.image} alt={catch_.species} pending={pending} />
      <div className="text-center px-1">
        <p className={`font-serif font-black italic text-base leading-tight mb-1 truncate ${
          pending ? 'text-[#2D3436]/40' : 'text-[#2D3436]'
        }`}>
          {catch_.nickname || catch_.species}
        </p>
        {catch_.nickname && (
          <p className={`font-serif italic text-xs leading-tight mb-1 truncate ${
            pending ? 'text-[#3B4D3C]/30' : 'text-[#3B4D3C]/60'
          }`}>
            {catch_.species}
          </p>
        )}
        <p className="text-[9px] font-bold uppercase tracking-widest text-[#3B4D3C]/50">
          {pending ? 'Venter på nett...' : new Date(catch_.caughtAt).toLocaleDateString('no-NO')}
        </p>
      </div>
    </div>
  );
}

// Bilde-zoom modal
function ImageZoomModal({ image, onClose }) {
  return (
    <div 
      className="fixed inset-0 z-[300] bg-[#2D3436]/98 backdrop-blur-sm flex items-center justify-center p-6" 
      onClick={onClose}
    >
      <button 
        className="absolute top-10 right-6 p-4 text-white hover:scale-110 transition-transform"
        onClick={onClose}
        aria-label="Lukk zoom"
      >
        <X size={32}/>
      </button>
      <img 
        src={image} 
        className="max-w-full max-h-[85vh] shadow-2xl border-4 border-white rounded-sm" 
        alt="Zoom" 
      />
    </div>
  );
}